import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import Link from "next/link";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { usePreviewModeContext } from "@contexts/preview-mode-context";

import TippyTooltip from "@stadium/ui/src/components/tooltip/tippy-tooltip";

import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { getAuthName } from "@stadium/redux-utils/selectors";
import { useModal } from "@stadium/hooks/lib/use-modal";

import {
  getIsFeaturedStore,
  getStoreFeUrl,
  getStoreLongDescription,
  getStoreName,
  getStoreSlug,
  getStoreType,
} from "@selectors/store";

import ShareLinkModal from "@components/modals/demo-mode/share-link";
import HowItWorksModal from "@components/modals/demo-mode/how-it-works";

import { isEshopStore, hideAccountStoreDetails } from "@helpers/store";

import { getStorePreviewPath, getStoreDetailPageRoute } from "@constants/routes";
import { HOW_IT_WORKS_MODAL, SHARE_LINK_MODAL } from "@constants/modal-types";
import { getShareShopEmailBody, getShareShopEmailSubject } from "@constants/share-shops-widget";
import { CUSTOMER_CASE_STUDY_STORE_DATA } from "@constants/demo-bar";

import styles from "./main.module.scss";

const DropdownToggleDemoMode = ({ demoDropdownOpen, setDemoDropdownOpen }) => {
  const storeName = useSelector(getStoreName);
  const storeLongDescription = useSelector(getStoreLongDescription);
  const storeCode = useSelector(getStoreSlug);
  const storeType = useSelector(getStoreType);
  const storeBasePath = useSelector(getStoreFeUrl);
  const isFeaturedStore = useSelector(getIsFeaturedStore);
  const userName = useSelector(getAuthName);
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { previewMode } = usePreviewModeContext();
  const { cTitle, cDescription, cAnchorLink, cCtaText } = CUSTOMER_CASE_STUDY_STORE_DATA;

  const {
    openModal: openShareLinkModal,
    renderModal: renderShareLinkModal,
    isOpen: isShareLinkModalOpen,
  } = useModal(SHARE_LINK_MODAL, ShareLinkModal);
  const {
    openModal: openHowItWorksModal,
    renderModal: renderHowItWorksModal,
    isOpen: isHowItWorksModalOpen,
  } = useModal(HOW_IT_WORKS_MODAL, HowItWorksModal);

  useEffect(() => {
    if (demoDropdownOpen) setDemoDropdownOpen(false);
  }, [isShareLinkModalOpen, isHowItWorksModalOpen]);

  const shareLinkModalData = {
    title: storeName,
    shareUrl: `${getStorePreviewPath({ storeBasePath })}\n\n`,
    emailSubject: getShareShopEmailSubject({ userName, storeName }),
    emailBody: getShareShopEmailBody({ userName, storeName }),
    isMobileDevice,
  };

  const renderStoreData = (title, description, linkTo, ctaText) => (
    <>
      <p className={styles.demoTitle}>{title}</p>
      <p className={styles.demoDescription}>{description}</p>
      {ctaText && (
        <Link
          href={linkTo}
          className={styles.linkText}
          target="_blank"
          rel="noopener"
          prefetch={false}
        >
          {ctaText}
        </Link>
      )}
    </>
  );

  const getStoreDetails = () => {
    const data = {
      ctaText: hideAccountStoreDetails(storeType, "View Full Details", cCtaText),
    };
    if (isFeaturedStore) {
      return {
        ...data,
        title: cTitle,
        description: cDescription,
        linkTo: cAnchorLink,
      };
    } else if (isEshopStore(storeType) && !isFeaturedStore) {
      return {
        title: storeName,
        description: storeLongDescription,
      };
    } else {
      return {
        ...data,
        title: storeName,
        description: storeLongDescription,
        linkTo: getStoreDetailPageRoute({ storeCode }),
      };
    }
  };

  const { title, ctaText, description, linkTo } = getStoreDetails();

  return (
    <BottomIconHideWrapper hideIcon={previewMode}>
      <div className={styles.navWrapperFlex}>
        <div
          className={styles.fixedBarToggleCenterBlock}
          onClick={() => {
            setDemoDropdownOpen(!demoDropdownOpen);
          }}
        >
          {!isMobileDevice && (
            <div className={styles.fixedBarToggleBlockShopName}>
              {demoDropdownOpen ? "Hide Details" : "Show Details"}
            </div>
          )}
          <div>
            <span className={`${styles.iconChevronUp} ${demoDropdownOpen ? styles.active : ""}`} />
          </div>
        </div>
      </div>
      <div className={styles.contentContainer}>
        {demoDropdownOpen && !(isHowItWorksModalOpen || isShareLinkModalOpen) && (
          <div className={styles.navWrapperStoreName}>
            {renderStoreData(title, description, linkTo, ctaText)}
            <div className={styles.demoContentFooterButtonsRow}>
              <div className={styles.demoContentFooterButton}>
                <button onClick={openShareLinkModal} className={styles.btnShare} />
              </div>
              <div className={styles.demoContentFooterButton}>
                <TippyTooltip
                  content={
                    <div className={styles.demoContentFooterButtonToolTip}>HOW IT WORKS</div>
                  }
                  interactive={true}
                  placement="top-end"
                  isLightTheme={true}
                  arrow={true}
                >
                  <button onClick={openHowItWorksModal} className={styles.btnInfo} />
                </TippyTooltip>
              </div>
            </div>
          </div>
        )}
        {renderShareLinkModal(
          {
            data: { ...shareLinkModalData },
          },
          false,
        )}
        {renderHowItWorksModal(
          {
            data: { isMobileDevice },
          },
          false,
        )}
      </div>
    </BottomIconHideWrapper>
  );
};

export default DropdownToggleDemoMode;
